import { useContext, useEffect, useId, useRef, useState, type FormEvent, type ReactNode, type Ref } from "react";
import { nachGruppe } from "../../../lib/feiertage";
import { Link, Navigate, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, PageHead } from "../../../components/ui";
import { describeError } from "../../../components/States";
import { NurLesenFeld } from "../../../app/nurLesen";
import { usePortalSperre } from "../../../app/sperreKontext";
import { StaffWorkspaceContext } from "../../../app/session";
import { useArbeitsbereich, useTarifKatalog, useTarifStand } from "../../../components/Tarif";
import { loadSetupStatus } from "./data";
import { listText, missingForSeite, type SettingsSection } from "./model";
import { EINST, PROFIL_WEG, SICHERHEIT_WEG, karteAktiv, karteGesperrt, kartenGruppen, sperrEtikett, type Karte, type PersoenlichKey, type SeiteKey } from "./baum";
import "./einstellungen.css";
import "./einstellungen-vollbild.css";
import { t, ta } from "../../../lib/sprache";

/** Lokale Bausteine der Seitengruppe (Rahmen der Einstellungen, Formularfelder, Download). Markup und Klassen aus dem Paket. */

export const DEMO_NOTE = () => t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictional.");
export const RIGHTS_NOTE = () => t("Diese Aktion ist für dein Konto nicht freigegeben. Wende dich an deinen Admin.", "This action is not permitted for your account. Contact your admin.");
/**
 * I-2 (IA-1): Agentur-Einstellungen bearbeitet nur der Admin. Wer eine solche Seite direkt aufruft (Manager), landet auf der
 * Startseite „Alle Einstellungen“ mit seinen persönlichen Punkten, statt auf „nicht freigegeben“.
 */
export function NurAdmin() {
  return <Navigate to={EINST} replace />;
}
export const CHEF_ONLY = () => t("Einstellungen, Team und Abo verwaltet der Admin deiner Agentur. Wende dich an deinen Admin.", "Settings, team and subscription are managed by your agency's admin. Contact your admin.");

export function useSetupStatus(orgId: string | null) {
  return useQuery({ queryKey: ["org_setup_status", orgId], enabled: !!orgId, queryFn: () => loadSetupStatus(api, orgId!) });
}

/**
 * IA-1 (21.10.2026, Zoho-Vorbild): links im Vollbild „Alle Einstellungen“ der ganze Einstellungsbaum. Gruppen klappen auf,
 * die Gruppe der offenen Seite steht offen, die aktive Seite (bzw. der aktive Anker) ist markiert. Unter 900 px steht statt
 * des Baums oben „‹ Alle Einstellungen“ (Startseite mit allen Karten).
 */
/**
 * I-1: Tarifrechte der Karten aus derselben Quelle wie „Was ist in meinem Tarif an“ (org_entitlements). `geladen` erst mit
 * Rolle und Tarifstand: bis dahin bleibt die Kartenliste leer (Ladezustand), statt kurz alle Karten zu zeigen.
 */
export function useKartenSperre() {
  const ws = useArbeitsbereich();
  const stand = useTarifStand(ws?.isChef ? ws.org.org_id : null);
  const katalog = useTarifKatalog();
  const creator = !!ws?.isCreator;
  return {
    ws,
    geladen: !!ws && (!ws.isChef || !!stand.data || !!stand.error),
    sperre: (k: Karte) => (stand.data && karteGesperrt(k, stand.data.limits) ? sperrEtikett(k, katalog.data, creator) : null),
  };
}

export function EinstellungenBaum({ seite }: { seite: SeiteKey }) {
  const { ws, geladen, sperre } = useKartenSperre();
  const { hash } = useLocation();
  const gruppen = geladen ? kartenGruppen({ chef: !!ws?.isChef, creator: !!ws?.isCreator }) : [];
  return (
    <nav className="tk-einst-nav tk-einst-baum" aria-label={t("Alle Einstellungen", "All settings")} aria-busy={!geladen}>
      <Link className="tk-einst-baum-start" to={EINST}>{t("Alle Einstellungen", "All settings")}</Link>
      {gruppen.map(({ gruppe, karten }) => {
        const offen = karten.some((k) => k.seite === seite);
        return (
          <details key={`${gruppe.key}-${offen ? "offen" : "zu"}`} className="tk-einst-gruppe" open={offen}>
            <summary className="tk-einst-gruppe-titel">{gruppe.titel()}</summary>
            {karten.map((k) => {
              const aktiv = karteAktiv(k, seite, hash);
              const zu = sperre(k);
              // Gesperrt im Tarif: Etikett „ab Team“ und der Weg zum Upgrade statt zur Seite
              return (
                <Link key={k.key} className={`${aktiv ? "current" : ""}${zu ? " tk-einst-gesperrt" : ""}`.trim()} aria-current={aktiv ? "page" : undefined} to={zu ? zu.to : k.to}>
                  {k.titel()}{zu && <span className="pill tk-einst-etikett">{zu.text}</span>}
                </Link>
              );
            })}
          </details>
        );
      })}
    </nav>
  );
}

/** Persönliche Seiten (Profilmenü): kleine Leiste mit Mein Profil, Anmeldung & Sicherheit, Benachrichtigungen; im Arbeitsrahmen. */
function PersoenlichNav({ current }: { current: PersoenlichKey }) {
  const ws = useArbeitsbereich();
  const eintraege = [
    { key: "profil", to: PROFIL_WEG, label: ws?.isCreator ? t("Mein Zugang", "My access") : t("Mein Profil", "My profile") },
    { key: "sicherheit", to: SICHERHEIT_WEG, label: t("Anmeldung & Sicherheit", "Sign-in & security") },
    { key: "benachrichtigungen", to: "/agentur/benachrichtigungen#kanaele", label: t("Benachrichtigungen", "Notifications") },
  ];
  return (
    <nav className="tk-einst-nav" aria-label={t("Persönliche Einstellungen", "Personal settings")}>
      <div className="tk-einst-gruppe" role="group" aria-labelledby="einst-gruppe-persoenlich">
        <div className="tk-einst-gruppe-titel" id="einst-gruppe-persoenlich">{t("Persönlich", "Personal")}</div>
        {eintraege.map((e) => (
          <Link key={e.key} className={e.key === current ? "current" : ""} aria-current={e.key === current ? "page" : undefined} to={e.to}>{e.label}</Link>
        ))}
      </div>
      {ws?.isChef && (
        <div className="tk-einst-gruppe" role="group" aria-labelledby="einst-gruppe-agentur">
          <div className="tk-einst-gruppe-titel" id="einst-gruppe-agentur">{ws.isCreator ? t("Konto", "Account") : t("Agentur", "Agency")}</div>
          <Link to={EINST}>{t("Alle Einstellungen", "All settings")}</Link>
        </div>
      )}
    </nav>
  );
}

/**
 * Sprung zu einem Anker (#bank, #sicherheit …), sobald das Ziel gezeichnet ist (Seiten laden ihre Daten nach): zugeklappte
 * Abschnitte und <details> auf dem Weg dorthin öffnen, hinscrollen und das Ziel kurz mit einem Rahmen zeigen.
 */
export function useAnkerSprung() {
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash || typeof document === "undefined") return;
    const id = decodeURIComponent(hash.slice(1));
    let versuche = 0;
    let timer = 0;
    const springe = () => {
      const el = document.getElementById(id);
      if (!el) {
        if (++versuche < 50) timer = window.setTimeout(springe, 100);
        return;
      }
      for (let p: HTMLElement | null = el; p; p = p.parentElement) {
        if (p instanceof HTMLDetailsElement) p.open = true;
        if (p.classList.contains("tk-klapp-inhalt") && p.hidden) p.parentElement?.querySelector<HTMLButtonElement>(".tk-klapp-kopf")?.querySelector("button")?.click();
      }
      if (el.classList.contains("tk-klapp-kopf")) {
        const knopf = el.querySelector("button");
        if (knopf?.getAttribute("aria-expanded") === "false") knopf.click();
      }
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("tk-anker-ziel");
      window.setTimeout(() => el.classList.remove("tk-anker-ziel"), 2400);
    };
    timer = window.setTimeout(springe, 60);
    return () => window.clearTimeout(timer);
  }, [hash, key]);
}

export type RahmenSeite = SeiteKey | PersoenlichKey;

/**
 * Rahmen jeder Seite der Einstellungen. Agentur-Themen (IA-1): Vollbild „Alle Einstellungen“ mit dem Baum links und dem
 * Formular (höchstens 860 px) rechts; unter 900 px oben „‹ Alle Einstellungen“. Persönliches (Mein Profil, Anmeldung &
 * Sicherheit): im Arbeitsrahmen mit der kleinen persönlichen Leiste.
 */
export function EinstellungenRahmen({ current, children }: { current: RahmenSeite; children: ReactNode }) {
  useAnkerSprung();
  // 0425: hinter der Portal-Sperre (Export, Abo & Tarif) ohne Einstellungsbaum; der schlanke Rahmen hat schon ein main
  const sperre = usePortalSperre();
  if (sperre) return <div className="tk-einst-inhalt tk-sperre-seite">{children}</div>;
  if (current === "profil" || current === "sicherheit") {
    return (
      <main id="main" className="content tk-einst">
        <PersoenlichNav current={current} />
        <div className="tk-einst-inhalt">
          {children}
        </div>
      </main>
    );
  }
  return (
    <main id="main" className="content tk-einst tk-einst-voll">
      <EinstellungenBaum seite={current} />
      <div className="tk-einst-inhalt">
        <Link className="tk-einst-zurueck" to={EINST}>‹ {t("Alle Einstellungen", "All settings")}</Link>
        {children}
      </div>
    </main>
  );
}

/** Bereiche, die in der Nur-Lesen-Phase bedienbar bleiben (Abo, Export) bzw. selbst sperren (Team: Support-Zugang bleibt). */
const LESBAR_OHNE_SPERRE: readonly SettingsSection[] = ["abo", "export", "team"];

/** Rahmen einer Einstellungsseite: Kopf wie im Paket, Einstellungsbaum, offene Pflichtangaben dieser Seite. */
export function SettingsFrame({ section, seite, orgId, title, text = "", eyebrow, notice, actions, children }: {
  section: SettingsSection;
  /** IA-1: Seite im Baum (Standard: der Bereich); die früheren Konditionen sind mehrere Seiten */
  seite?: SeiteKey;
  orgId: string;
  title: string;
  text?: ReactNode;
  eyebrow?: string | undefined;
  notice?: ReactNode;
  /** Knöpfe rechts im Seitenkopf */
  actions?: ReactNode;
  children: ReactNode;
}) {
  const setup = useSetupStatus(orgId);
  const hier: SeiteKey = seite ?? (section === "konditionen" ? "provision" : section);
  const missing = missingForSeite(setup.data, hier);
  const creator = !!useContext(StaffWorkspaceContext)?.isCreator;
  return (
    <EinstellungenRahmen current={hier}>
      <PageHead eyebrow={eyebrow ?? (creator ? t("Dein Creator-Konto", "Your creator account") : ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account"))} title={title} text={text}>{actions}</PageHead>
      {notice}
      {missing.length > 0 && (
        <div className="notice" role="status">
          {t("Für die Einrichtung fehlt hier noch: ", "Still missing for setup here: ")}{listText(missing)}.
          {section === "firma" && t(" Verträge lassen sich erst versenden, wenn Firmenname, vertretungsberechtigte Person, anwendbares Recht und Gerichtsstand eingetragen sind.", " Contracts can only be sent once company name, authorised representative, governing law and place of jurisdiction are entered.")}
        </div>
      )}
      {/* Nur-Lesen-Phase (RECHTE-R3-3): Formulare gesperrt statt Knöpfe, die sicher scheitern; Abo und Export bleiben, Team sperrt selbst je Bereich */}
      {LESBAR_OHNE_SPERRE.includes(section) ? children : <NurLesenFeld>{children}</NurLesenFeld>}
      {api.mode === "demo" && <p className="demo-note">{DEMO_NOTE()}</p>}
    </EinstellungenRahmen>
  );
}

// ── Formularfelder: Beschriftung immer sichtbar über dem Feld, Fehler und Hinweis per aria-describedby ──

interface FieldProps {
  label: ReactNode;
  error?: string | undefined;
  hint?: ReactNode;
}
function useDescribed(error?: string, hint?: ReactNode) {
  const id = useId();
  const hintId = hint ? `${id}-hinweis` : undefined;
  const errorId = error ? `${id}-fehler` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;
  return { hintId, errorId, describedBy, invalid: error ? true : undefined };
}
function FieldExtras({ hint, hintId, error, errorId }: { hint?: ReactNode; hintId?: string | undefined; error?: string | undefined; errorId?: string | undefined }) {
  return (
    <>
      {hint && <small id={hintId}>{hint}</small>}
      {error && <small id={errorId} className="tk-error">{error}</small>}
    </>
  );
}

export function TextField({ label, error, hint, value, onChange, type = "text", ...rest }: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string | undefined;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  autoComplete?: string;
  list?: string;
  maxLength?: number;
  min?: string;
  max?: string;
  inputMode?: "text" | "numeric" | "decimal" | "email" | "tel" | "url";
}) {
  const d = useDescribed(error, hint);
  return (
    <label>
      {label}
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={d.invalid} aria-describedby={d.describedBy} {...rest} />
      <FieldExtras hint={hint} hintId={d.hintId} error={error} errorId={d.errorId} />
    </label>
  );
}

export function AreaField({ label, error, hint, value, onChange, rows = 4, ...rest }: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string | undefined;
  disabled?: boolean;
  textareaRef?: Ref<HTMLTextAreaElement>;
}) {
  const d = useDescribed(error, hint);
  const { textareaRef, ...props } = rest;
  return (
    <label>
      {label}
      <textarea ref={textareaRef} rows={rows} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={d.invalid} aria-describedby={d.describedBy} {...props} />
      <FieldExtras hint={hint} hintId={d.hintId} error={error} errorId={d.errorId} />
    </label>
  );
}

export function SelectField({ label, error, hint, value, onChange, options, disabled, placeholder }: FieldProps & {
  value: string;
  onChange: (v: string) => void;
  /** group: Optionen mit Gruppe stehen in <optgroup> (Rundgang R-20, Feiertagsregionen nach Land) */
  options: readonly { key: string; label: string; group?: string }[];
  disabled?: boolean;
  /** leere erste Auswahl („Bitte wählen“) */
  placeholder?: string;
}) {
  const d = useDescribed(error, hint);
  const option = (o: { key: string; label: string }) => <option key={o.key} value={o.key}>{o.label}</option>;
  return (
    <label>
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={d.invalid} aria-describedby={d.describedBy} disabled={disabled}>
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.some((o) => o.group)
          ? nachGruppe(options).map(([g, os]) => (g ? <optgroup key={g} label={g}>{os.map(option)}</optgroup> : os.map(option)))
          : options.map(option)}
      </select>
      <FieldExtras hint={hint} hintId={d.hintId} error={error} errorId={d.errorId} />
    </label>
  );
}

export function CheckField({ label, checked, onChange, disabled, hint, error }: FieldProps & { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  const d = useDescribed(error, hint);
  return (
    <>
      <label className="row tk-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} aria-describedby={d.describedBy} aria-invalid={d.invalid} />
        <span>{label}</span>
      </label>
      <FieldExtras hint={hint} hintId={d.hintId} error={error} errorId={d.errorId} />
    </>
  );
}

/**
 * Aufklappbare Zeile „Weitere Angaben …“ (Einrichtung entschärfen, 07.10.2026): Fachliches bleibt vollständig erreichbar,
 * ohne die erste Ansicht zu überladen. Steht darin ein Fehler (`open`), klappt sie von selbst auf; schließen kann man sie danach
 * wieder selbst.
 */
export function WeitereAngaben({ summary, open = false, children }: { summary: string; open?: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    if (open && ref.current) ref.current.open = true;
  }, [open]);
  return (
    <details ref={ref} className="tk-weitere-angaben">
      <summary>{summary}</summary>
      <div className="stack">{children}</div>
    </details>
  );
}

/** Zusammenfassung über dem Absenden-Knopf: wo etwas fehlt oder nicht passt. */
export function FormErrors({ errors, error }: { errors: Record<string, string>; error?: string | null }) {
  const list = Object.values(errors);
  if (!list.length && !error) return null;
  return (
    <div className="tk-error" role="alert">
      {list.length > 0 ? (list.length === 1 ? list[0] : t(`Bitte ${list.length} Angaben prüfen: ${list.slice(0, 3).join(" ")}${list.length > 3 ? " …" : ""}`, `Please check ${list.length} details: ${list.slice(0, 3).join(" ")}${list.length > 3 ? " …" : ""}`)) : error}
    </div>
  );
}

/** Welcher Absenden-Knopf ausgelöst hat („speichern“ oder „weiter“). */
export function submitMode(e: FormEvent<HTMLFormElement>): "speichern" | "weiter" {
  const s = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null;
  return s?.value === "weiter" ? "weiter" : "speichern";
}

/** Entwurf eines Formulars mit Setzer je Feld. */
export function useDraft<T extends object>(initial: T) {
  const [draft, setDraft] = useState<T>(initial);
  const set = <K extends keyof T>(k: K) => (v: T[K]) => setDraft((d) => ({ ...d, [k]: v }));
  return { draft, setDraft, set };
}

export function Confirm({ open, title, text, confirmLabel, danger, busy, onConfirm, onClose, extra, disabled }: {
  open: boolean;
  title: string;
  text: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Eingaben zwischen Text und Knöpfen (Auswahl, Bestätigung) */
  extra?: ReactNode;
  /** Bestätigen erst möglich, wenn die Eingaben passen */
  disabled?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <p>{text}</p>
      {extra && <div className="stack">{extra}</div>}
      <div className="row">
        <button type="button" className={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy || disabled} aria-busy={busy}>{busy ? t("Wird ausgeführt …", "Processing …") : confirmLabel}</button>
        <button type="button" className="" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

export function InlineError({ error, retry }: { error: unknown; retry?: () => void }) {
  const d = describeError(error);
  return (
    <div className="panel stack tk-empty" role="alert">
      <h3>{d.title}</h3>
      <p>{d.text}</p>
      {retry && <div className="row"><button onClick={retry}>{t("Erneut versuchen", "Try again")}</button></div>}
    </div>
  );
}

/** Datei im Browser erzeugen und herunterladen (Export). Nichts verlässt den Browser. */
export function downloadFile(name: string, content: string, type: string): void {
  downloadBlob(name, new Blob([content], { type }));
}
export function downloadBlob(name: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Adresse der Website (Preise, Rechtliches); gleiche Quelle wie der Website-Rahmen. */
export { WEBSITE_URL } from "../../../app/nav";
